import React from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  Link,
  IconButton,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { COLORS } from "colors/color";
import logoImage from "../../assets/img/logo_.svg";
import smallLogoImage from "../../assets/img/logo_small.svg";
import { useTranslation } from "react-i18next"; // Import useTranslation

const JobCountBanner = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  background: "linear-gradient(90deg, #007EF2 0%, #ABD7FF 100%)",
  color: "white",
  padding: theme.spacing(1),
  textAlign: "center",
}));

const Footer = () => {
  const { t } = useTranslation(); // Initialize useTranslation
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

  return (
    <Box 
      component="footer" 
      sx={{
        width: "100%",
        pt: {
          xs: 2,
          sm: 3,
          md: 4,
          lg: "5%"
        }
      }}
    >
      <Container maxWidth="lg">
        {/* Top Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: { xs: "center", md: "space-between" },
            alignItems: { xs: "center", md: "flex-start" },
            gap: { xs: 4, md: 2, lg: 4 },
            mb: { xs: 4, sm: 5, md: 6 },
          }}
        >
          {/* Logo and Description */}
          <Box 
            sx={{ 
              width: { xs: "100%", md: "50%", lg: "60%" },
              textAlign: { xs: "center", md: "left" }
            }}
          >
            <img
              src={isMobile ? smallLogoImage : logoImage}
              alt="Logo"
              style={{ 
                width: isMobile ? "20px" : isTablet ? "100px" : "150px",
                marginBottom: "16px"
              }}
            />

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: { xs: "100%", md: "450px" },
                px: { xs: 2, md: 0 },
                fontSize: { xs: "14px", sm: "15px" },
                lineHeight: { xs: "24px", sm: "27px" },
                fontFamily: "Poppins",
                fontWeight: 400,
              }}
            >
              {t("footer.description")} {/* Translated description */}
            </Typography>
          </Box>

          {/* Newsletter Subscription */}
          <Box
            sx={{
              width: { xs: "100%", md: "45%", lg: "40%" },
              textAlign: { xs: "center", md: "left" }
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: COLORS.title_color,
                mb: { xs: 2, md: 3 },
                textTransform: "none",
                fontFamily: "Kontora",
                fontWeight: 700,
                fontSize: { xs: "18px", sm: "20px" },
              }}
            >
              {t("footer.newsletterTitle")} {/* Translated newsletter title */}
            </Typography>
            <Stack 
              direction={{ xs: "column", sm: "row" }} 
              spacing={1.5}
              alignItems="center"
            >
              <TextField
                placeholder={t("footer.emailPlaceholder")} 
                variant="outlined"
                size="small"
                sx={{
                  width: { xs: "90%", sm: "342px" },
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 48, sm: 56, md:54 },
                    borderRadius: "30px",
                    fontFamily: "Poppins",
                    backgroundColor: "#fff",
                    boxShadow: "0px 4px 40px #0000001A",
                    paddingLeft:1,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#fff",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                      borderWidth: "2px",
                      boxShadow: "0px 0px 5px 0.4px #0085FF",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                      borderWidth: "2px",
                      boxShadow: "0px 0px 5px 0.4px #0085FF",
                    },
                  },
                }}
              />
              <Button
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: "30px",
                  backgroundColor: COLORS.button_color,
                  textTransform: "none",
                  px: { xs: 4, sm: 3 },
                  width: { xs: "150px", sm: "100.44px" },
                  height: { xs: "45px", sm: "50.79px" },
                }}
              >
                {t("footer.sendButton")} {/* Translated send button */}
              </Button>
            </Stack>
          </Box>
        </Box>

        <Divider
          sx={{
            mb: { xs: 2, sm: 3 },
            mt: { xs: "5%", md: "8%", lg: "0%" },
            borderColor: COLORS.button_color,
            borderWidth: "0.5px",
            width: "100%",
          }}
        />

        {/* Bottom Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: { xs: 2, sm: 3 },
            py: { xs: 0, sm: 0 },
          }}
        >
          {/* Copyright */}
          <Typography
            variant="body2"
            color={COLORS.button_color}
            sx={{
              fontWeight: 400,
              fontSize: { xs: "13px", sm: "15px" },
              fontFamily: "Poppins",
              order: { xs: 3, md: 1 },
            }}
          >
            {t("footer.copyright")} {/* Translated copyright */}
          </Typography>

          {/* Navigation Links */}
          <Stack
            direction="row"
            spacing={{ xs: 2, sm: 4 }}
            sx={{
              order: { xs: 1, md: 2 },
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            {[
              t("footer.links.offers"),
              t("footer.links.about"),
              t("footer.links.contactUs"),
              t("footer.links.privacy"),
            ].map((text) => (
              <Link
                key={text}
                href="/home"
                underline="none"
                sx={{
                  color: COLORS.button_color,
                  fontSize: { xs: "13px", sm: "15px" },
                  "&:hover": {
                    color: "primary.main",
                  },
                }}
              >
                {text}
              </Link>
            ))}
          </Stack>

          {/* Social Media Icons */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              order: { xs: 2, md: 3 },
            }}
          >
            {[
              { icon: <FacebookIcon />, label: t("footer.socialMedia.facebook") },
              { icon: <InstagramIcon />, label: t("footer.socialMedia.instagram") },
              { icon: <LinkedInIcon />, label: t("footer.socialMedia.linkedin") },
            ].map((social) => (
              <IconButton
                key={social.label}
                color="primary"
                aria-label={social.label}
                sx={{
                  padding: { xs: 0.5, sm: 1 },
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "primary.dark",
                  },
                }}
              >
                {social.icon}
              </IconButton>
            ))}
          </Stack>
        </Box>
      </Container>
      <JobCountBanner />
    </Box>
  );
};

export default Footer;